import { NumberFormatter } from "../../../services/number-formatter";
import { ColumnBuilder, RowBuilder } from "../../../shared/ui/table/builders";

import type { IAdKeywordsTableRow, IAdKeywordsTableRowWithEmpty } from "./ad-keywords-table-data.types";

// Define the API response type based on the actual data structure
interface IAdKeywordApiResponse {
    keyword: string;
    clicks?: number;
    totalClicks?: number;
    prevClicks?: number;
    poP?: number;
    volume?: number;
    avgRank?: number;
}

type AdKeywordsSortFields = keyof IAdKeywordsTableRow;

const createAdKeywordsMultiSorter = (primaryField: AdKeywordsSortFields) => {
    const sortOrder: Array<AdKeywordsSortFields> = [primaryField, "volume", "clicks", "avgRank", "poP", "keyword"];

    const reorderedFields = sortOrder.filter((field, index) =>
        field === primaryField ? index === 0 : !sortOrder.slice(0, index).includes(field),
    );

    return reorderedFields.map((field) => (a: IAdKeywordsTableRow, b: IAdKeywordsTableRow) => {
        if (field === "keyword") {
            return (a.keyword as { data: string }).data.localeCompare((b.keyword as { data: string }).data);
        }

        if (field === "poP") {
            const aData = a.poP;
            const bData = b.poP;

            const getPoPSortValue = (data: IAdKeywordsTableRow["poP"]) => {
                if (data.data == null) {
                    return -2000000;
                }
                return data.data;
            };

            const aSortValue = getPoPSortValue(aData);
            const bSortValue = getPoPSortValue(bData);

            return bSortValue - aSortValue;
        }

        const aValue = (a[field] as { data: number }).data;
        const bValue = (b[field] as { data: number }).data;

        if (aValue == null && bValue == null) {
            return 0;
        }
        if (aValue == null) {
            return -1;
        }
        if (bValue == null) {
            return 1;
        }

        return aValue - bValue;
    });
};

function RemoveBorderRight() {
    return {
        style: {
            borderRight: "none",
        },
    };
}

export const adKeywordsColumnBuilder = new ColumnBuilder<IAdKeywordsTableRowWithEmpty>()
    .addColumn("empty", { title: "", fixed: true, resize: false, sorter: false, width: 12, onCell: RemoveBorderRight })
    .addColumn("keyword", {
        title: "monitor_and_protect.page.ads.keywords_table.columns.keywords.title",
        multiSorter: createAdKeywordsMultiSorter("keyword"),
        tooltipTitle: "monitor_and_protect.page.ads.keywords_table.columns.keywords.tooltip_title",
        tooltipSubtitle: "monitor_and_protect.page.ads.keywords_table.columns.keywords.tooltip_subtitle",
    })
    // Hiding PaidClicks and PoP columns until we have proper data coverage
    // .addColumn("clicks", {
    //     title: "monitor_and_protect.page.ads.keywords_table.columns.paidClicks.title",
    //     width: 200,
    //     resize: false,
    //     align: "left",
    //     multiSorter: createAdKeywordsMultiSorter("clicks"),
    //     tooltipTitle: "monitor_and_protect.page.ads.keywords_table.columns.paidClicks.tooltip_title",
    //     tooltipSubtitle: "monitor_and_protect.page.ads.keywords_table.columns.paidClicks.tooltip_subtitle",
    //     formatter: "collapse",
    // })
    // .addColumn("poP", {
    //     title: "monitor_and_protect.page.ads.keywords_table.columns.popChange.title",
    //     width: 140,
    //     resize: false,
    //     align: "right",
    //     multiSorter: createAdKeywordsMultiSorter("poP"),
    //     tooltipTitle: "monitor_and_protect.page.ads.keywords_table.columns.popChange.tooltip_title",
    //     tooltipSubtitle: "monitor_and_protect.page.ads.keywords_table.columns.popChange.tooltip_subtitle",
    // })
    .addColumn("volume", {
        title: "monitor_and_protect.page.ads.keywords_table.columns.volume.title",
        width: 140,
        resize: false,
        align: "right",
        multiSorter: createAdKeywordsMultiSorter("volume"),
        tooltipTitle: "monitor_and_protect.page.ads.keywords_table.columns.volume.tooltip_title",
        tooltipSubtitle: "monitor_and_protect.page.ads.keywords_table.columns.volume.tooltip_subtitle",
        formatter: "collapse",
    })
    .addColumn("avgRank", {
        title: "monitor_and_protect.page.ads.keywords_table.columns.avgPosition.title",
        width: 120,
        resize: false,
        align: "right",
        multiSorter: createAdKeywordsMultiSorter("avgRank"),
        tooltipTitle: "monitor_and_protect.page.ads.keywords_table.columns.avgPosition.tooltip_title",
        tooltipSubtitle: "monitor_and_protect.page.ads.keywords_table.columns.avgPosition.tooltip_subtitle",
    });

export const adKeywordsRowBuilder = new RowBuilder<IAdKeywordApiResponse, IAdKeywordsTableRowWithEmpty>(
    (keyword) => keyword.keyword,
    adKeywordsColumnBuilder,
)
    .mapField("empty", () => ({
        type: "text",
        data: "",
        text: "",
    }))
    .mapField("keyword", (keyword) => ({
        type: "text",
        data: keyword.keyword,
        text: keyword.keyword,
    }))
    .mapField("clicks", (keyword) => {
        const clicks = keyword.clicks ?? 0;
        const totalClicks = keyword.totalClicks ?? 0;
        const progressValue = totalClicks > 0 ? clicks / totalClicks : 0;

        return {
            type: "progress",
            data: clicks,
            label: NumberFormatter.toAbbr(clicks, 0),
            value: progressValue,
            variant: "detailed",
        };
    })
    .mapField("poP", (keyword) => ({
        type: "change",
        data: keyword.poP ?? 0,
        value: keyword.poP ?? 0,
        isLost: false,
        isNew: false,
        formattedValue: NumberFormatter.toSmartPercent((keyword.poP ?? 0) / 100),
    }))
    .mapField("volume", (keyword) => ({
        type: "number",
        data: keyword.volume ?? 0,
        formatter: "toAbbr",
        minValue: 50,
    }))
    .mapField("avgRank", (keyword) => ({
        type: "number",
        data: keyword.avgRank ?? 0,
        text: keyword.avgRank?.toString() ?? "—",
    }));
